'use client'

import * as React from 'react'
import * as Dialog from 'primitives/dialog'
import { useTranslations } from 'next-intl'
import { usePublicUserProfileQuery } from 'codegen/generated/user'
import {
  useSocialUserFollowersInfiniteQuery,
  useSocialUserFollowingInfiniteQuery,
} from '../../_hooks/use-followers-infinite'
import { useFollowersDialog } from '../contexts/followers-dialog-context'
import { Button } from '@repo/design-system-cf/components/button'
import { UserCard } from '../user-card'
import { IconClose } from '@repo/design-system-cf/components/icons'
import { Spinner } from '@repo/design-system-cf/components/spinner'
import { formatUserName } from 'utils/format-user-name'
import { useUser } from '@repo/cf-user/hooks/use-user-data'
import type { PublicUserProfile } from 'codegen/types'

type Tab = 'followers' | 'following'
type PublicUserProfileWithoutLocation = Omit<PublicUserProfile, 'location'>

// oxlint-disable-next-line complexity
export function CrafterFollowersDialog() {
  const { targetSlug, isOpen, closeDialog, initialTab } = useFollowersDialog()
  const t = useTranslations('social-feed.page.crafter_profile')
  const user = useUser()

  const [activeTab, setActiveTab] = React.useState<Tab>(initialTab ?? 'followers')
  const previousIsOpenRef = React.useRef(isOpen)

  React.useEffect(() => {
    const wasOpen = previousIsOpenRef.current
    previousIsOpenRef.current = isOpen

    if (!wasOpen && isOpen) {
      setActiveTab(initialTab ?? 'followers')
    }
  }, [isOpen, initialTab])

  const scrollRef = React.useRef<HTMLDivElement>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)

  const scrollPositionsRef = React.useRef<Record<Tab, number>>({
    followers: 0,
    following: 0,
  })

  React.useEffect(() => {
    if (!isOpen) {
      scrollPositionsRef.current = { followers: 0, following: 0 }
    }
  }, [isOpen])

  React.useEffect(() => {
    if (!isOpen) {
      return
    }

    const scrollElement = scrollRef.current
    if (!scrollElement) {
      return
    }

    const savedTop = scrollPositionsRef.current[activeTab] ?? 0

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollElement.scrollTop = savedTop
      })
    })

    const fallback = setTimeout(() => {
      if (scrollElement.scrollTop !== savedTop) {
        scrollElement.scrollTop = savedTop
      }
    }, 100)

    return () => clearTimeout(fallback)
  }, [isOpen, activeTab])

  const { data: publicUserProfile } = usePublicUserProfileQuery(
    { slug: targetSlug || '' },
    { select: data => data?.publicUserProfile, enabled: Boolean(targetSlug) },
  )

  const isOwn = Boolean(user.data?.id) && (!targetSlug || targetSlug === user.data?.slug)
  const userId = publicUserProfile?.id || ''
  const fullUserName = formatUserName(
    publicUserProfile?.firstName,
    publicUserProfile?.lastName,
    'User',
  )

  const {
    data: followersPages,
    hasNextPage: hasMoreFollowers,
    fetchNextPage: fetchMoreFollowers,
    isFetchingNextPage: isFetchingMoreFollowers,
    isLoading: isLoadingFollowers,
  } = useSocialUserFollowersInfiniteQuery(userId)

  const {
    data: followingPages,
    hasNextPage: hasMoreFollowing,
    fetchNextPage: fetchMoreFollowing,
    isFetchingNextPage: isFetchingMoreFollowing,
    isLoading: isLoadingFollowing,
  } = useSocialUserFollowingInfiniteQuery(userId)

  const followers = React.useMemo(
    () => followersPages?.pages?.flatMap(page => page?.userFollowers?.followers ?? []) ?? [],
    [followersPages],
  )

  const following = React.useMemo(
    () => followingPages?.pages?.flatMap(page => page?.userFollowing?.following ?? []) ?? [],
    [followingPages],
  )

  const tryLoadMore = React.useCallback(
    async (element?: HTMLDivElement | null) => {
      const scrollElement = element ?? scrollRef.current
      if (!scrollElement) {
        return
      }

      const distanceFromBottom =
        scrollElement.scrollHeight - scrollElement.scrollTop - scrollElement.clientHeight

      if (distanceFromBottom > 60) {
        return
      }

      if (activeTab === 'followers') {
        if (isFetchingMoreFollowers || !hasMoreFollowers) {
          return
        }
        await fetchMoreFollowers()
      } else {
        if (isFetchingMoreFollowing || !hasMoreFollowing) {
          return
        }
        await fetchMoreFollowing()
      }
    },
    [
      activeTab,
      fetchMoreFollowers,
      fetchMoreFollowing,
      hasMoreFollowers,
      hasMoreFollowing,
      isFetchingMoreFollowers,
      isFetchingMoreFollowing,
    ],
  )

  const handleScroll = React.useCallback(
    async (event: React.UIEvent<HTMLDivElement>) => {
      const element = event.currentTarget
      const scrollTop = element.scrollTop <= 1 ? 0 : element.scrollTop
      scrollPositionsRef.current[activeTab] = scrollTop
      await tryLoadMore(element)
    },
    [activeTab, tryLoadMore],
  )

  const handleTabChange = React.useCallback(
    (nextTab: Tab) => {
      const scrollElement = scrollRef.current
      if (scrollElement) {
        const currentTop = scrollElement.scrollTop
        scrollPositionsRef.current[activeTab] = currentTop <= 1 ? 0 : currentTop
      }
      setActiveTab(nextTab)
    },
    [activeTab],
  )

  React.useEffect(() => {
    if (!isOpen) {
      return
    }

    const hasMore = activeTab === 'followers' ? hasMoreFollowers : hasMoreFollowing
    if (!hasMore) {
      return
    }

    const scrollElement = scrollRef.current
    void tryLoadMore(scrollElement)
  }, [
    isOpen,
    activeTab,
    followers.length,
    following.length,
    hasMoreFollowers,
    hasMoreFollowing,
    tryLoadMore,
  ])

  const isLoadingActiveTab = activeTab === 'followers' ? isLoadingFollowers : isLoadingFollowing

  return (
    <Dialog.Root open={isOpen} onOpenChange={open => !open && closeDialog()} defaultOpen>
      <Dialog.Portal>
        <Dialog.Body
          className="font-poppins z-50 flex items-stretch justify-center min-h-screen"
          overlayZIndex={1102}
          zIndex={1102}
          onOpenAutoFocus={event => {
            event.preventDefault()
          }}
        >
          <div
            ref={containerRef}
            tabIndex={-1}
            className="
              bg-cf-white sm:rounded-4 rounded-t-[12px] relative overflow-hidden sm:p-6 p-4 pr-2 sm:pr-4
              lg:max-w-[880px] w-full tablet:max-w-[696px]
              h-screen tablet:h-auto
              tablet:max-h-[min(690px,100vh)] xl:max-h-[min(780px,100vh)]
              flex flex-col focus-visible:outline-none focus-visible:shadow-none
            "
          >
            <p className="sm:text-[28px] text-16 text-cf-sf-gray-100 sm:pb-6 pb-4 font-bold">
              {t('followers_dialog_title')}
            </p>

            <div className="flex sm:pb-6 pb-4">
              <Button
                variant={activeTab === 'followers' ? 'tertiary' : 'ghost'}
                onClick={() => handleTabChange('followers')}
                className="rounded-full"
              >
                {t('followers_tab_title')}
              </Button>
              <Button
                variant={activeTab === 'following' ? 'tertiary' : 'ghost'}
                className="ml-2 rounded-full"
                onClick={() => handleTabChange('following')}
              >
                {t('following_tab_title')}
              </Button>
            </div>

            <div ref={scrollRef} onScroll={handleScroll} className="flex-1 min-h-0 overflow-y-auto">
              <div className="grid lg:grid-cols-4 sm:grid-cols-3 gap-4 items-start">
                {activeTab === 'followers'
                  ? followers
                      .filter((userItem): userItem is PublicUserProfileWithoutLocation =>
                        Boolean(userItem && userItem.id),
                      )
                      .map((member, index) => (
                        <UserCard
                          key={`followers-${member.id}-${index}`}
                          user={member}
                          ownUserID={user.data?.id}
                        />
                      ))
                  : null}

                {activeTab === 'followers' && hasMoreFollowers ? (
                  <div className="pb-4 col-span-full flex items-center justify-center">
                    {isFetchingMoreFollowers ? <Spinner /> : null}
                  </div>
                ) : null}

                {activeTab === 'followers' &&
                followers.length === 0 &&
                !hasMoreFollowers &&
                !isLoadingActiveTab ? (
                  <div className="col-span-full text-16 sm:text-20 text-cf-sf-gray-1100 font-bold">
                    {isOwn
                      ? t('empty_followers')
                      : t('empty_user_followers', { user: fullUserName })}
                  </div>
                ) : null}

                {activeTab === 'following'
                  ? following
                      .filter((userItem): userItem is PublicUserProfileWithoutLocation =>
                        Boolean(userItem && userItem.id),
                      )
                      .map((member, index) => (
                        <UserCard key={`following-${member.id}-${index}`} user={member} />
                      ))
                  : null}

                {activeTab === 'following' && hasMoreFollowing ? (
                  <div className="pb-4 col-span-full flex items-center justify-center">
                    {isFetchingMoreFollowing ? <Spinner /> : null}
                  </div>
                ) : null}

                {activeTab === 'following' &&
                following.length === 0 &&
                !hasMoreFollowing &&
                !isLoadingActiveTab ? (
                  <div className="col-span-full text-16 sm:text-20 text-cf-sf-gray-1100 font-bold">
                    {isOwn
                      ? t('empty_following')
                      : t('empty_user_following', { user: fullUserName })}
                  </div>
                ) : null}
              </div>
            </div>

            <Dialog.Close asChild className="absolute sm:top-4 sm:right-4 top-3 right-2">
              <Button variant="ghost" className="w-9 h-9 rounded-2">
                <IconClose className="text-cf-purple-900 cursor-pointer" />
              </Button>
            </Dialog.Close>
          </div>
        </Dialog.Body>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
